import { useCallback, useEffect } from 'react';

import { getCachedMute, getCachedVolume } from '../util/cachedVolume';

interface UseCachedVolumeProps {
  volumeEnabled: boolean;
  onUpdateVolume: (volume: number) => void;
  onUpdateMute: (mute: boolean) => void;
}

/**
 * Hook to retrieve cached volume and mute status
 * then update the volume and mute status using provided callbacks
 * @param volumeEnabled - boolean to determine if volume is enabled
 * @param onUpdateVolume - callback to update volume
 * @param onUpdateMute - callback to update mute status
 */
export default ({
  volumeEnabled,
  onUpdateVolume,
  onUpdateMute,
}: UseCachedVolumeProps): void => {
  const retrieveCachedVolume = useCallback(async () => {
    const volume = await getCachedVolume();

    /** should only update volume if cache exists */
    if (typeof volume === 'number') {
      onUpdateVolume(volume);
    }
  }, [onUpdateVolume]);

  const retrieveCachedMute = useCallback(async () => {
    const mute = await getCachedMute();

    /** should only update mute if there the cache exists */
    if (typeof mute === 'boolean') {
      onUpdateMute(mute);
    }
  }, [onUpdateMute]);

  useEffect(() => {
    if (!volumeEnabled) return;

    retrieveCachedVolume();
  }, [retrieveCachedVolume, volumeEnabled]);

  useEffect(() => {
    if (!volumeEnabled) return;

    retrieveCachedMute();
  }, [retrieveCachedMute, volumeEnabled]);
};
